import { RISK_PROFILES } from '../assumptions/india'
import type { AssetKey } from '../engine/types'

/**
 * Risk profile as a chip row driving a live allocation bar.
 *
 * A `<select>` would be less code and would be the wrong call: this is the
 * control that most changes the shape of the fan, and hiding it behind a
 * dropdown makes the most interesting thing on the screen the least discoverable.
 * The bar exists so the choice has a visible consequence *before* the chart
 * redraws — you see the mix change under your thumb, then watch the fan follow.
 */

interface RiskProfilePickerProps {
  profileId: string
  onChange: (profileId: string) => void
}

/**
 * Ordered most to least risky. This is an *ordinal* series, not a categorical
 * one — the order carries the meaning — so it takes a single-hue lightness ramp
 * rather than four separate hues. That also leaves the scenario hues free for F4
 * and keeps gold reserved for the FIRE goal, which is the one colour on the
 * screen allowed to mean something specific.
 */
/*
 * The ramp is derived from `--c-accent` (see `app.css`), so the bar belongs to the
 * plan being edited along with the chips above it.
 *
 * The first pass at the accent fix left this on the fan ramp, reasoning that the
 * bar encodes the asset mix rather than the scenario. That is true of what each
 * SEGMENT means and beside the point for what the STRIP is: it sits directly under
 * the risk chips, changes only when they do, and reading purple chips above a teal
 * bar is the same split the accent token exists to remove. Repointing the whole
 * ramp — not just its top step — is what keeps the ordinal reading intact.
 */
const SLEEVES: { key: AssetKey; label: string; token: string }[] = [
  { key: 'equity', label: 'Equity', token: '--c-accent-ramp-1' },
  { key: 'debt', label: 'Debt', token: '--c-accent-ramp-2' },
  { key: 'gold', label: 'Gold', token: '--c-accent-ramp-3' },
  { key: 'cash', label: 'Cash', token: '--c-accent-ramp-4' },
]

export function RiskProfilePicker({ profileId, onChange }: RiskProfilePickerProps) {
  const active = RISK_PROFILES.find((p) => p.id === profileId) ?? RISK_PROFILES[1]

  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <span
          id="risk-profile-label"
          className="text-[length:var(--text-label)] text-[color:var(--c-text-secondary)]"
        >
          Risk profile
        </span>
        <span className="text-[length:var(--text-micro)] text-[color:var(--c-text-tertiary)]">
          {active.blurb}
        </span>
      </div>

      <div
        role="radiogroup"
        aria-labelledby="risk-profile-label"
        className="mt-2 flex gap-2"
      >
        {RISK_PROFILES.map((profile) => {
          const selected = profile.id === active.id
          return (
            <button
              key={profile.id}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(profile.id)}
              className="flex-1 rounded-[var(--radius-full)] border text-[length:var(--text-label)] transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out-quart)]"
              style={{
                minHeight: 'var(--touch-target)',
                borderColor: selected ? 'transparent' : 'var(--c-border)',
                background: selected ? 'var(--c-accent)' : 'var(--c-bg-surface)',
                color: selected ? 'var(--c-text-on-accent)' : 'var(--c-text-secondary)',
              }}
            >
              {profile.label}
            </button>
          )
        })}
      </div>

      <AllocationBar allocation={active.allocation} />
    </div>
  )
}

function AllocationBar({ allocation }: { allocation: Record<AssetKey, number> }) {
  return (
    <div className="mt-3">
      {/* 2px gaps rather than borders between segments: a border adds a line that
          reads as data, a gap just separates. */}
      <div className="flex h-2 gap-[2px]" aria-hidden="true">
        {SLEEVES.map(({ key, token }) => (
          <div
            key={key}
            className="h-full rounded-[var(--radius-full)] transition-[flex-grow] duration-[var(--dur-base)] ease-[var(--ease-out-expo)]"
            style={{
              flexGrow: Math.max(allocation[key], 0.0001),
              flexBasis: 0,
              background: `var(${token})`,
            }}
          />
        ))}
      </div>

      {/* Identity is never carried by colour alone — the percentages are the
          accessible reading of the bar above, and honestly the faster one. */}
      <p className="mt-1.5 text-[length:var(--text-micro)] text-[color:var(--c-text-muted)]">
        {SLEEVES.filter(({ key }) => allocation[key] > 0)
          .map(({ key, label }) => `${label} ${Math.round(allocation[key] * 100)}%`)
          .join(' · ')}
      </p>

      {/*
        This line is load-bearing, not boilerplate. An asset mix presented
        without it is the closest thing in this product to "advice on investment
        portfolio containing securities" — the second limb of SEBI's definition
        of investment advice, and the one people miss because they are busy
        being pleased that the tool names no individual stocks or funds.

        The word "illustrative" is doing the work: these are inputs to a
        simulation the user is running, not portfolios being put to them.
      */}
      <p className="mt-1 text-[length:var(--text-micro)] text-[color:var(--c-text-muted)]">
        Illustrative mixes to simulate — not recommended portfolios.
      </p>
    </div>
  )
}
